import React, { useState, useMemo, useEffect, useCallback, useRef } from "react"
import { Text, VStack } from "@chakra-ui/react"
import StatusToggleWorkflow, { WorkflowState, WorkflowMessages } from "./StatusToggleWorkflow"
import { AssetKind } from "@hydrologiq/gql-experience/graphql"
import VolumePressureInputs, { VolumePressureInputsRef } from "../figures/asset/VolumePressureInputs"

export interface MonitoringActivationWorkflowProps {
  // Required props
  open: boolean
  action?: "activate" | "deactivate" // Which action to perform when opened

  // Asset information (optional, needed for dynamic_storage volume/pressure inputs)
  assetKind?: AssetKind | null
  assetId?: string | null

  // Default values for volume/pressure inputs
  defaultVolume?: number | null
  defaultMinPressure?: number | null

  // Callbacks
  onClose: () => void
  onSuccess: () => void
  onError: (error: any) => void
  onRefresh?: () => void | Promise<void>

  // Mutation callbacks - handled by parent
  // onActivate can now accept optional volume and min_pressure parameters
  onActivate: (volume?: number | null, min_pressure?: number | null) => Promise<void>
  onDeactivate: (volume?: number | null, min_pressure?: number | null) => Promise<void>

  // Optional pre-activation conflict detection
  onPreActivate?: () => Promise<boolean | string> // Returns false/error message if conflict
  onConflict?: (message: string) => void // Handle conflict display

  // Optional customization
  entityName?: string // "asset" | "system" etc.
  confirmActivation?: boolean // Whether to show confirmation dialog for activation (default: false)

  // Portal/positioning
  container?: React.RefObject<HTMLElement | null> // Container for dialog portal

  // Custom content
  confirmationContent?: React.ReactNode
  customTitles?: Partial<Record<WorkflowState, string>>
}

export interface MonitoringActivationWorkflowState {
  workflowState: WorkflowState
  pendingAction: "activate" | "deactivate" | null
}

const MonitoringActivationWorkflow = ({
  open,
  action,
  assetKind,
  assetId,
  onClose,
  onSuccess,
  onError,
  onRefresh,
  onActivate,
  onDeactivate,
  onPreActivate,
  onConflict,
  entityName = "monitoring",
  confirmActivation = false,
  container,
  confirmationContent,
  customTitles = {},
  defaultVolume,
  defaultMinPressure,
}: MonitoringActivationWorkflowProps) => {
  // Internal workflow state
  const [workflowState, setWorkflowState] = useState<WorkflowState>("idle")
  const [pendingAction, setPendingAction] = useState<"activate" | "deactivate" | null>(null)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)

  // Ref to access volume/pressure values - no context needed!
  const volumeInputsRef = useRef<VolumePressureInputsRef>(null)

  // Check if this is a dynamic_storage asset
  // Handle different case formats and ensure robust comparison
  const isDynamicStorage = useMemo(() => {
    if (!assetKind) return false

    // Check exact match first
    if (assetKind === AssetKind.DynamicStorage) return true

    // Check case-insensitive comparison
    const assetKindLower = assetKind.toString().toLowerCase()
    const dynamicStorageLower = AssetKind.DynamicStorage?.toString().toLowerCase()

    if (assetKindLower === dynamicStorageLower) return true

    // Check direct string comparison for snake_case
    if (assetKindLower === "dynamic_storage") return true

    return false
  }, [assetKind])

  // Handle activation
  const handleActivate = useCallback(async () => {
    if (pendingAction !== "activate") {
      setPendingAction("activate")
    }

    // Get values from ref instead of context
    const values = volumeInputsRef.current?.getValues() ?? { volume: defaultVolume?.toString() ?? "", minPressure: defaultMinPressure?.toString() ?? "" }
    // Validate volume and min_pressure for dynamic_storage assets
    if (isDynamicStorage) {
      const volumeNum = values.volume.trim() ? parseFloat(values.volume.trim()) : null
      const minPressureNum = values.minPressure.trim() ? parseFloat(values.minPressure.trim()) : null

      if (volumeNum === null && minPressureNum === null) {
        const errorMsg = "Volume or minimum pressure are required for dynamic storage assets"
        setErrorMessage(errorMsg)
        // Keep workflowState as "confirming" to show inline error
        onError(new Error(errorMsg))
        return
      }

      if (volumeNum !== null && volumeNum <= 0) {
        const errorMsg = "Volume must be greater than 0"
        setErrorMessage(errorMsg)
        // Keep workflowState as "confirming" to show inline error
        onError(new Error(errorMsg))
        return
      }

      if (minPressureNum !== null && minPressureNum < 0) {
        const errorMsg = "Minimum pressure must be greater than or equal to 0"
        setErrorMessage(errorMsg)
        // Keep workflowState as "confirming" to show inline error
        onError(new Error(errorMsg))
        return
      }
    }

    // Optional pre-activation conflict detection
    if (onPreActivate) {
      try {
        const conflictResult = await onPreActivate()
        if (conflictResult !== true) {
          // Conflict detected - show conflict state
          const conflictMessage = typeof conflictResult === "string" ? conflictResult : "Activation conflict detected"
          setWorkflowState("conflict")

          // Call conflict handler if provided
          if (onConflict) {
            onConflict(conflictMessage)
          }
          return
        }
      } catch (error) {
        console.error(`Pre-activation check failed for ${entityName}:`, error)
        const errorMsg = error instanceof Error ? error.message : "Pre-activation check failed"
        setErrorMessage(errorMsg)
        setWorkflowState("error")
        onError(error)
        return
      }
    }

    if (workflowState !== "processing") {
      setWorkflowState("processing")
    }

    try {
      // Parse volume and min_pressure values from ref
      const volumeNum = values.volume.trim() ? parseFloat(values.volume.trim()) : null
      const minPressureNum = values.minPressure.trim() ? parseFloat(values.minPressure.trim()) : null

      await onActivate(volumeNum, minPressureNum)

      // Show success immediately after successful activation (no validation)
      setErrorMessage(null) // Clear any previous errors
      setWorkflowState("success")

      onSuccess()

      // Note: onRefresh and dialog close will be handled when user manually closes success dialog
    } catch (error) {
      console.error(`Failed to activate ${entityName}:`, error)
      const errorMsg = error instanceof Error ? error.message : `Failed to activate ${entityName}`
      setErrorMessage(errorMsg)
      setWorkflowState("error")
      onError(error)
    }
  }, [
    pendingAction,
    workflowState,
    onActivate,
    onPreActivate,
    onConflict,
    onRefresh,
    onSuccess,
    onClose,
    entityName,
    onError,
    isDynamicStorage,
    defaultVolume,
    defaultMinPressure,
  ])

  // Handle deactivation
  const handleDeactivate = async () => {
    setPendingAction("deactivate")
    setWorkflowState("processing")

    try {
      // Get values from ref (optional for deactivation)
      const values = volumeInputsRef.current?.getValues() ?? { volume: "", minPressure: "" }
      const volumeNum = values.volume.trim() ? parseFloat(values.volume.trim()) : null
      const minPressureNum = values.minPressure.trim() ? parseFloat(values.minPressure.trim()) : null

      await onDeactivate(volumeNum, minPressureNum)

      // Show success immediately after successful deactivation (no validation)
      setErrorMessage(null) // Clear any previous errors
      setWorkflowState("success")

      onSuccess()

      // Note: onRefresh and dialog close will be handled when user manually closes success dialog
    } catch (error) {
      console.error(`Failed to deactivate ${entityName}:`, error)
      const errorMsg = error instanceof Error ? error.message : `Failed to deactivate ${entityName}`
      setErrorMessage(errorMsg)
      setWorkflowState("error")
      onError(error)
    }
  }

  // Reset volume/pressure when dialog closes
  useEffect(() => {
    if (!open && workflowState === "idle") {
      volumeInputsRef.current?.reset()
    }
  }, [open, workflowState])

  // Handle workflow cancellation - only allow during idle/confirming states
  const handleCancelWorkflow = async () => {
    // Don't allow cancellation during processing - user must wait
    if (workflowState === "processing" || workflowState === "cancelling") {
      return
    }

    // Standard cancellation - just close
    setWorkflowState("idle")
    setPendingAction(null)
    setErrorMessage(null) // Clear error message
    onClose()
  }

  // Handle dialog close
  const handleCloseDialog = async () => {
    // Don't allow close during processing or cancelling
    if (workflowState === "processing" || workflowState === "cancelling") {
      return
    }

    // If closing from success state, transition to confirmed state for cleanup
    if (workflowState === "success") {
      setWorkflowState("confirmed")
      return
    }

    // For all other states, just close normally
    handleCancelWorkflow()
  }

  // Generate workflow messages
  const workflowMessages = useMemo((): WorkflowMessages | null => {
    if (!pendingAction) return null
    return {
      processing: `Please wait while ${entityName} is being ${pendingAction}d.`,
      cancelling: `Cancelling ${entityName} ${pendingAction} and reverting changes...`,
      success:
        pendingAction === "activate"
          ? "Note that it may take up to 5 minutes for the initial data to come through. You can check the monitoring status for the latest state."
          : `${entityName.charAt(0).toUpperCase() + entityName.slice(1)} has been successfully ${pendingAction}d.`,
      error:
        errorMessage ||
        `The request to ${pendingAction} ${entityName} has failed or timed out. Please contact the Hydrologiq support team or try again.`,
    }
  }, [pendingAction, entityName, errorMessage])

  // Sync workflow state with parent open/action props
  useEffect(() => {
    if (open && action && workflowState === "idle") {
      setPendingAction(action)
      if (action === "activate") {
        if (confirmActivation) {
          // For activation with confirmation, show confirmation dialog first
          setWorkflowState("confirming")
        } else {
          // For activation without confirmation, trigger activation immediately (includes pre-activation checks)
          // Trigger activation in the next tick to avoid state update conflicts
          setTimeout(() => {
            handleActivate()
          }, 0)
        }
      } else {
        // For deactivation, show confirmation dialog
        setWorkflowState("confirming")
      }
    } else if (!open && workflowState !== "idle") {
      // Reset state when closed
      setWorkflowState("idle")
      setPendingAction(null)
      setErrorMessage(null) // Clear error message
    }
  }, [open, action, workflowState, confirmActivation]) // Removed handleActivate to prevent double execution

  // Handle confirmed state - perform cleanup and refresh
  useEffect(() => {
    if (workflowState === "confirmed") {
      const performCleanup = async () => {
        try {
          // Call onRefresh to update the data
          if (onRefresh) {
            await Promise.resolve(onRefresh())
          }
        } catch (error) {
          // Log refresh errors but don't let them prevent workflow completion
          console.error(`Failed to refresh data after ${entityName} workflow:`, error)
        } finally {
          // Always reset workflow state and close, even if refresh failed
          setWorkflowState("idle")
          setPendingAction(null)
          onClose()
        }
      }

      performCleanup()
    }
  }, [workflowState, entityName])

  // Static confirmation content (without inputs)
  const staticConfirmationContent = useMemo(() => {
    if (confirmationContent) return confirmationContent

    if (workflowState === "confirming" && pendingAction) {
      if (pendingAction === "activate") {
        return (
          <Text>
            Are you sure you want to <strong>activate</strong> {entityName}?
          </Text>
        )
      } else {
        return (
          <Text>
            Are you sure you want to <strong>deactivate</strong> {entityName}?
            <Text mt={2} fontSize="sm" color="gray.600">
              This will stop all monitoring activities.
            </Text>
          </Text>
        )
      }
    }

    return null
  }, [confirmationContent, workflowState, pendingAction, entityName])

  // Memoized volume/pressure inputs component
  // Use ref pattern - component manages its own state, no re-renders when parent state changes
  const volumePressureInputs = useMemo(() => {
    if (!isDynamicStorage || workflowState !== "confirming" || pendingAction !== "activate") {
      return null
    }
    // No onChange needed - we check ref values directly when needed
    return (
      <VolumePressureInputs
        ref={volumeInputsRef}
        required
        defaultVolume={defaultVolume}
        defaultMinPressure={defaultMinPressure}
        error={errorMessage}
      />
    )
  }, [isDynamicStorage, workflowState, pendingAction, errorMessage, defaultVolume, defaultMinPressure])

  // Compose final confirmation content
  const finalConfirmationContent = useMemo(() => {
    if (!staticConfirmationContent) return null

    return (
      <VStack align="stretch" gap={4}>
        {staticConfirmationContent}
        {volumePressureInputs}
      </VStack>
    )
  }, [staticConfirmationContent, volumePressureInputs])

  // Default custom titles
  const defaultTitles = useMemo(
    () => ({
      confirming: pendingAction === "activate" ? `Activate ${entityName}` : `Deactivate ${entityName}`,
      processing: "Processing Request",
      success: pendingAction === "activate" ? "Monitoring activated" : "Success",
      ...customTitles,
    }),
    [pendingAction, entityName, customTitles]
  )

  // Only render when workflow should be open
  if (!open && workflowState === "idle") {
    return null
  }

  return (
    <StatusToggleWorkflow
      workflowState={workflowState}
      pendingAction={pendingAction}
      messages={workflowMessages}
      onConfirm={pendingAction === "activate" ? handleActivate : handleDeactivate}
      onCancel={workflowState === "processing" || workflowState === "cancelling" ? undefined : handleCancelWorkflow}
      onClose={handleCloseDialog}
      container={container}
      confirmationContent={finalConfirmationContent}
      customTitles={defaultTitles}
      showSkipButton={false}
    />
  )
}

// memoize the component
const MemoizedMonitoringActivationWorkflow = React.memo(MonitoringActivationWorkflow)
export default MemoizedMonitoringActivationWorkflow

// Helper hook for using the workflow
export const useMonitoringActivationWorkflow = () => {
  const [isOpen, setIsOpen] = useState(false)
  const [action, setAction] = useState<"activate" | "deactivate" | null>(null)

  const openActivation = () => {
    setAction("activate")
    setIsOpen(true)
  }

  const openDeactivation = () => {
    setAction("deactivate")
    setIsOpen(true)
  }

  const close = () => {
    setIsOpen(false)
    setAction(null)
  }

  return {
    isOpen,
    action,
    openActivation,
    openDeactivation,
    close,
  }
}
